/**
 * @file        hash.js
 *              General purpose utility routines related to creating hashes for use
 *              within the DCP platform.
 *
 *              Someday, we will need to support multiple hashTypes for the same resource.
 *              The prefix will be used to differentiate them during a transitional period.
 *
 * @author      Wes Garland, wes@kingsds.network
 * @date        Jul 2021
 */

const wallet = require('../dcp-client/wallet');
const { assert } = require('./dcp-assert');

/** 
 * Supported hash types.  Each hashType also becomes a module export.
 * 
 * prefix:    plain text prefix prepended to hash for easy disambiguation
 * fun:       the actual implementation of the hash function
 * serialize: the function used to serialize message objects for this hash function
 */
const hashTypes = {
  eh0: { prefix: '',     fun: eh0, serialize: JSON.stringify },
  eh1: { prefix: 'eh1-', fun: eh1, serialize: JSON.stringify },
};
Object.assign(exports, hashTypes);

/** 
 * Implement the eh0 hash algorithm, which is just a wrapper for ethereum's
 * hashPersonalMessage keccak-256 code
 */
function eh0(str)
{
  const ethUtil = wallet._internalEth.util;
  const Buffer  = wallet._internalEth.buffer.Buffer;
  var hash = ethUtil.hashPersonalMessage(Buffer.from(str)).toString('hex');
  return hash;
}

/** 
 * Implement the eh1 hash algorithm, which is just a wrapper for ethereum's
 * hashPersonalMessage keccak-256 code that adds a prefix
 */
function eh1(str)
{
  const ethUtil = wallet._internalEth.util;
  const Buffer  = wallet._internalEth.buffer.Buffer;
  var hash = ethUtil.hashPersonalMessage(Buffer.from(str)).toString('hex');
  return hashTypes.eh1.prefix + hash;
}

/**
 * Calculate a message hash, with optional salt.
 *
 * @param {object}        type         Type of hash to use; must be a property of hashTypes
 * @param {string|object} message      Message to hash; objects will be converted to strings automatically.
 * @param {string}        [salt]       Optional salt to prepend onto message
 */
exports.calculate = function hash$$calculate(type, message, salt)
{
  assert(Object.values(hashTypes).indexOf(type) !== -1);

  if (typeof message === 'object')
    message = type.serialize(message);

  if (typeof salt !== 'undefined')
    message = salt + message;
  
  return type.fun(message, salt);
}
